/**
 * Small module-singleton bridges that let the AI assistant's tool handlers
 * (host realm, in useAiExtensionActions) hand work to a mounted extension tab
 * and get a result back — mirroring the request-builder send-handler ref and
 * the collection-runner stashed-resolve-ref used for Collections, but without
 * threading refs through App → EditorPane → the tab.
 *
 * Two bridges:
 * - DB run: db_run_sql opens an auto-run query tab; DbManagerResultsTab runs it
 *   and resolves here with metadata ONLY (row count + column names/types) —
 *   never row data, honoring the "AI never sees results" contract.
 * - Doc editor: write_document applies content to the live Lexical editor of an
 *   open document tab (so autosave persists it and the user sees it update),
 *   falling back to a direct store write when the doc isn't open.
 */

// ── DB run bridge ────────────────────────────────────────────────────────────

const dbRuns = new Map(); // aiRunId -> { resolve }

/** Register a pending AI SQL run; returns a promise the tab resolves with metadata. */
export function createAiDbRun(aiRunId, { timeoutMs = 30000 } = {}) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      if (dbRuns.has(aiRunId)) {
        dbRuns.delete(aiRunId);
        resolve({ executed: false, error: 'Timed out waiting for the query to run' });
      }
    }, timeoutMs);
    dbRuns.set(aiRunId, {
      resolve: (meta) => {
        clearTimeout(timer);
        resolve(meta);
      },
    });
  });
}

/** Called by DbManagerResultsTab after the auto-run completes (metadata only). */
export function resolveAiDbRun(aiRunId, meta) {
  const entry = dbRuns.get(aiRunId);
  if (!entry) return;
  dbRuns.delete(aiRunId);
  entry.resolve(meta);
}

// ── Doc editor bridge ────────────────────────────────────────────────────────

const docEditors = new Map(); // documentId -> { applyBody, getBody }

/** DocumentEditorTab registers its live-apply API on mount. Returns an unregister fn. */
export function registerAiDocEditor(documentId, api) {
  docEditors.set(documentId, api);
  return () => {
    if (docEditors.get(documentId) === api) docEditors.delete(documentId);
  };
}

/** The live editor API for an open document, or null when it isn't open. */
export function getAiDocEditor(documentId) {
  return docEditors.get(documentId) || null;
}
